import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, VFC } from 'react';

import { useDispatch } from 'react-redux';
import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';

import { usePlayerContext } from '../../player/PlayerContext';
import { usePipContext } from '../../player/PictureInPictureProvider';
import {
  addOrientationChangeListener,
  useDeviceOrientation,
} from '../utils/useDeviceOrientation';
import { isMobile, isTablet } from '../../utils/device';
import {
  clearCurrentlyPlayingAsset,
  setCurrentlyPlayingAsset,
} from '../../modules/ui/actions';
import { playerContainer } from '../../player/playerContainer';
import { getAssetAspectRatio } from '../../shared/helpers/assetHelpers';
import { usePlayer } from '../../player/hooks/usePlayer';
import { OrientationType } from '../../utils/screenOrientation';
import { DEFAULT_ASSET_ASPECT_RATIO } from '../../player/playerAssetHelpers';

import useAssetWatchtime from './useAssetWatchtime';
import { withParentalControls } from './withParentalControls';

import * as styles from './Player.module.scss';

/*
Playback testing scheme:
  1. regular asset page
    1.1 open directly (SSR)
      1.1.1 open in regular browser (autoplay should work)
        - during playback start a different regular asset (autoplay should work)
      1.1.2 open in incognito on a vhost (autoplay will fail)
        - start a different regular asset (autoplay should work)
    1.2 open from a list (client side)
    1.3 check player errors
      - open a video which is geoblocked (should show an error)
      - start a different non-geoblocked asset
  2. documentary asset page
    2.1 open directly (SSR)
      2.1.1 not logged in
      2.1.2 logged in
    2.2 open from a list (client side)
      2.2.1 not logged in
      2.2.2 logged in

Getting paywalled content to work locally:
 - use a custom vhost which is a subdomain of vg.no (for example tv-local.vg.no)
 - use nginx or apache to access the app on port :80
 - the easiest (sic!) way to get this to work on android emulator and actual devices
   is to set up static hostnames on your router

In case you want to know why the above steps are necessary:
 - to enable SPiD login we must use the id-pre.vg.no domain as session domain,
   which on the other hand sets a Lax session cookie, so login works only on subdomains on vg.no
 - the paywall token API (api.vg.no/svp/token) on the other hand has CORS enabled
   for domains ending with vg.no, so any port other than :80 will cause CORS to fail
*/

export interface PlayerProps {
  asset: SvpAssetAdditional;
  className?: string;
  startTime?: number;
}

export const BasePlayer: VFC<PlayerProps> = ({
  asset,
  className,
  startTime,
}) => {
  const player = usePlayer();
  const { openAsset, stopPlayback, isCasting } = usePlayerContext();
  const { isPipEnabled } = usePipContext();
  const playerContainerRef = useRef<HTMLDivElement>(null!);
  const dispatch = useDispatch();
  const orientation = useDeviceOrientation();
  const isCastingRef = useRef(isCasting);
  const isPipEnabledRef = useRef(isPipEnabled);

  useEffect(() => {
    playerContainer.setPositionNode(playerContainerRef.current);
    playerContainer.show();
    return () => {
      playerContainer.hide();
      playerContainer.setPositionNode(null);
    };
  }, [asset?.id]);

  useEffect(() => {
    isCastingRef.current = isCasting;
  }, [isCasting]);

  useEffect(() => {
    isPipEnabledRef.current = isPipEnabled;
  }, [isPipEnabled]);

  useEffect(() => {
    if (!player || !(isMobile || isTablet)) {
      return;
    }

    const unsubscribe = addOrientationChangeListener((orientation) => {
      const jw = player.getJWPlayer();
      jw?.setFullscreen(orientation === 'landscape');
    });

    return () => unsubscribe();
  }, [player]);

  // memoize the asset to avoid the same as above until it actually changes
  const [memoizedAsset, setMemoizedAsset] = useState(asset);

  // not memoized asset object is passed here on purpose,
  // see `getShouldUpdate` function
  useAssetWatchtime(asset);

  useEffect(() => {
    if (asset.id !== memoizedAsset.id) {
      setMemoizedAsset(asset);
    }
  }, [asset, memoizedAsset.id]);

  useEffect(() => {
    openAsset({
      asset: memoizedAsset,
      seekTime: startTime,
    });

    dispatch(setCurrentlyPlayingAsset(memoizedAsset));

    return () => {
      if (isCastingRef.current || isPipEnabledRef.current) {
        playerContainer.hide();

        return;
      }

      dispatch(clearCurrentlyPlayingAsset());
    };
  }, [startTime, dispatch, memoizedAsset, openAsset]);

  useEffect(() => {
    return () => {
      if (isCastingRef.current || isPipEnabledRef.current) {
        return;
      }

      stopPlayback();
    };
  }, [stopPlayback]);

  const styleProp: CSSProperties = useMemo(
    () => ({
      aspectRatio: (orientation === OrientationType.PORTRAIT
        ? getAssetAspectRatio(asset)
        : DEFAULT_ASSET_ASPECT_RATIO
      ).toString(),
    }),
    [asset, orientation]
  );

  return (
    <div className={className}>
      <div
        className={styles.player}
        ref={playerContainerRef}
        style={styleProp}
      />
    </div>
  );
};

export const Player = withParentalControls(BasePlayer);
